import {
  useCallback, useEffect, useMemo, useState
} from 'react';
import {
  useForm, useFieldArray, UseFormReturn, FieldErrors
} from 'react-hook-form';

import {
  fields as fieldsData,
  FieldsByState,
  FIELD_NAMES,
  CONTACT_FORM_STATES,
  REWARDS_EXPERIENCE_OPTION_VALUE,
  ONLINE_CARRYOUT_OPTION_VALUE,
  ONLINE_DELIVERY_OPTION_VALUE,
  RESPONSE_METHODS
} from '../common/fields';

export interface UseContactFormProps {
  formId: string;
}

export interface UseContactFormReturn {
  register: UseFormReturn['register'];
  handleSubmit: UseFormReturn['handleSubmit'];
  errors: FieldErrors;
  control: UseFormReturn['control'];
  trigger: UseFormReturn['trigger'];
  getValues: UseFormReturn['getValues'];
  setValue?: UseFormReturn['setValue'];
  formState?: UseFormReturn['formState'];
  setError?: UseFormReturn['setError'];
  fields?: ContactFormFieldData[];
  fieldsByFieldset: ContactFormFieldData[];
  getFormFieldByName?: (name: string) => ContactFormField | undefined;
  getSibilingFieldByName?: (name: string) => ContactFormField | undefined;
  isFieldNextToAnother?: (name: string) => boolean | undefined;
  onSelectionChange?: (selectedOption: NameValuePair) => void;
  currentFieldset: ContactFormStates;
}

export const useContactForm = ({ formId }: UseContactFormProps): UseContactFormReturn => {
  const [optionalFields, setOptionalFields] = useState<string[]>([]);
  const [currentFieldset, setCurrentFieldset] = useState<ContactFormStates>(CONTACT_FORM_STATES.DEFAULT);
  const [lastSelection, setLastSelection] = useState<NameValuePair>();

  const {
    control, register, handleSubmit, trigger, formState: { errors }, getValues, setValue, formState, setError
  } = useForm<ContactFormField>({
    mode: 'onChange',
    defaultValues: {
      [FIELD_NAMES.MAIN_REASON]: '',
      [FIELD_NAMES.ORDER_METHOD]: '',
      [FIELD_NAMES.REWARDS]: '',
      [FIELD_NAMES.DEVICE_USED]: '',
      [FIELD_NAMES.FIRST_NAME]: '',
      [FIELD_NAMES.LAST_NAME]: '',
      [FIELD_NAMES.EMAIL]: '',
      [FIELD_NAMES.CONFIRM_EMAIL]: '',
      [FIELD_NAMES.PHONE]: '',
      [FIELD_NAMES.ORDER_DATE]: '',
      [FIELD_NAMES.ORDER_TIME]: '',
      [FIELD_NAMES.SHARED_FEEDBACK]: '',
      [FIELD_NAMES.AMOUNT_SPENT]: '',
      [FIELD_NAMES.STORE_LOCATION]: ''
    }
  });

  const { fields, append } = useFieldArray<ContactFormField>({
    control,
    name: formId || 'contact-form-generic-id'
  });

  useEffect(() => {
    // register fields with react-hook-form
    append(fieldsData);
  }, [append]);

  const getFormFieldByName = useCallback((name: string) => fields.find((i: ContactFormField) => i.name === name), [fields]);

  const getSibilingFieldByName = useCallback((name: string, fieldset?: ContactFormFieldData[]) => {
    const currentField = getFormFieldByName(name);

    return (fieldset || fields)?.find((f: ContactFormField) => f.name === (currentField as ContactFormField)?.nextTo);
  }, [getFormFieldByName, fields]);

  const isFieldNextToAnother = useCallback((name: string) => !!fields.some((f: ContactFormField) => f.nextTo === name) as boolean, [fields]);

  const updateOptionalFields = useCallback((selectedOption: NameValuePair) => {
    const { name = '', value = '' } = selectedOption;
    const originalOptionals = fields?.reduce((result: string[], f: ContactFormFieldData) => {
      if (f?.optional) {
        result.push(f?.name || '');
      }

      return result;
    }, []);

    if (name === FIELD_NAMES.RESPONSE_METHOD) {
      switch (value) {
        case RESPONSE_METHODS.EMAIL: {
          return setOptionalFields([...originalOptionals, FIELD_NAMES.PHONE]);
        }
        case RESPONSE_METHODS.PHONE: {
          return setOptionalFields([
            ...originalOptionals,
            FIELD_NAMES.EMAIL,
            FIELD_NAMES.CONFIRM_EMAIL
          ]);
        }
        case RESPONSE_METHODS.NO_REPLY_NEEDED: {
          return setOptionalFields([
            ...originalOptionals,
            FIELD_NAMES.PHONE,
            FIELD_NAMES.EMAIL,
            FIELD_NAMES.CONFIRM_EMAIL
          ]);
        }

        default: {
          return setOptionalFields(originalOptionals);
        }
      }
    }

    return setOptionalFields(originalOptionals);
  }, [setOptionalFields, fields]);

  const onSelectionChange = useCallback((selectedOption: NameValuePair) => {
    const { name = '', value = '' } = selectedOption;
    const field = getFormFieldByName(name);
    const optionData = (field as ContactFormField)?.options?.find((o: ContactFormFieldOption) => o?.value === value);
    const nextFieldset = optionData?.fieldset as ContactFormStates;

    if (nextFieldset) {
      setCurrentFieldset(nextFieldset);
    }

    setLastSelection(selectedOption);
    updateOptionalFields(selectedOption);
  }, [setCurrentFieldset, getFormFieldByName, updateOptionalFields, setLastSelection]);

  const shouldCollectRewardsIssue = useMemo(() => {
    const { name = '', value = '' } = lastSelection || {};

    if (name === FIELD_NAMES.MAIN_REASON) {
      return value === REWARDS_EXPERIENCE_OPTION_VALUE;
    }

    const values = getValues();
    // @ts-expect-error - existing code
    return values?.[FIELD_NAMES.MAIN_REASON] === REWARDS_EXPERIENCE_OPTION_VALUE;
  }, [getValues, lastSelection]);

  const shouldCollectDevice = useMemo(() => {
    const { name = '', value = '' } = lastSelection || {};

    if (name === FIELD_NAMES.ORDER_METHOD) {
      return value === ONLINE_CARRYOUT_OPTION_VALUE || value === ONLINE_DELIVERY_OPTION_VALUE;
    }

    const values = getValues();
    // @ts-expect-error - existing code
    const methodValue = values?.[FIELD_NAMES.ORDER_METHOD];

    return methodValue === ONLINE_CARRYOUT_OPTION_VALUE || methodValue === ONLINE_DELIVERY_OPTION_VALUE;
  }, [getValues, lastSelection]);

  const fieldsByFieldset = useMemo(() => {
    const fieldset = FieldsByState[currentFieldset]?.filter((f: string) => {
      if (!shouldCollectDevice && f === FIELD_NAMES.DEVICE_USED) {
        return false;
      }

      if (!shouldCollectRewardsIssue && f === FIELD_NAMES.REWARDS) {
        return false;
      }

      return true;
    });

    return fields?.reduce((result: ContactFormFieldData[], f: ContactFormFieldData) => {
      if (fieldset?.includes(f?.name || '')) {
        result.push({
          ...f,
          optional: optionalFields?.includes(f?.name || '') || false,
          required: optionalFields?.includes(f?.name || '') ? false : f?.required || false
        });
      }

      return result;
    }, []);
  }, [
    fields,
    currentFieldset,
    shouldCollectRewardsIssue,
    shouldCollectDevice,
    optionalFields
  ]);

  return {
    register,
    handleSubmit,
    errors,
    control,
    trigger,
    getValues,
    setValue,
    formState,
    setError,
    fields,
    fieldsByFieldset,
    getFormFieldByName,
    getSibilingFieldByName,
    isFieldNextToAnother,
    onSelectionChange,
    currentFieldset
  } as any;
};

export default useContactForm;
